/**
 * OASIS Field Configuration for MVP Implementation
 * Simplified version focusing on the most critical 30-40 OASIS items
 */

import { OasisFieldConfig, OasisCategories } from '../types/patientmr/oasis';

// Critical OASIS fields for MVP - focusing on most essential items
export const mvpOasisFields: OasisFieldConfig[] = [
  // Administrative - Basic Required Fields
  {
    key: 'M0030_startCareDate',
    label: 'Start of Care Date',
    type: 'date',
    required: true,
    category: OasisCategories.ADMINISTRATIVE,
    oasisItem: 'M0030',
    helpText: 'Date this episode of care began'
  },
  {
    key: 'M0100_thisAssessmentCompleted',
    label: 'This Assessment Completed',
    type: 'select',
    required: true,
    category: OasisCategories.ADMINISTRATIVE,
    oasisItem: 'M0100',
    options: [
      { value: '1', label: 'Start of care—further visits planned' },
      { value: '2', label: 'Start of care—no further visits planned' },
      { value: '3', label: 'Resumption of care (after inpatient stay)' },
      { value: '4', label: 'Recertification (follow-up) reassessment' },
      { value: '5', label: 'Other follow-up reassessment' },
      { value: '6', label: 'Significant change in condition/status' },
      { value: '7', label: 'Death at home' },
      { value: '8', label: 'Transfer to an inpatient facility' },
      { value: '9', label: 'Discharge from agency' }
    ]
  },

  // Primary Diagnosis - Critical for HIPPS
  {
    key: 'M1021_primaryDiagnosis',
    label: 'Primary Diagnosis',
    type: 'icd-select',
    required: true,
    category: OasisCategories.DIAGNOSES,
    oasisItem: 'M1021',
    helpText: 'Primary diagnosis ICD-10 code and description'
  },
  {
    key: 'M1023_primaryDiagnosisChange',
    label: 'Primary Diagnosis Change',
    type: 'select',
    required: true,
    category: OasisCategories.DIAGNOSES,
    oasisItem: 'M1023',
    options: [
      { value: '0', label: 'No' },
      { value: '1', label: 'Yes' }
    ]
  },

  // Risk Factors - Important for care planning
  {
    key: 'M1033_riskFactors',
    label: 'Risk Factors',
    type: 'multiselect',
    required: true,
    category: OasisCategories.DIAGNOSES,
    oasisItem: 'M1033',
    options: [
      { value: '1', label: 'Smoking' },
      { value: '2', label: 'Obesity' },
      { value: '3', label: 'Alcohol dependency' },
      { value: '4', label: 'Drug dependency' },
      { value: '5', label: 'None of the above' }
    ]
  },

  // Sensory Status - Critical for ADL assessment
  {
    key: 'M1200_vision',
    label: 'Vision',
    type: 'select',
    required: true,
    category: OasisCategories.SENSORY_STATUS,
    oasisItem: 'M1200',
    options: [
      { value: '0', label: 'Normal vision' },
      { value: '1', label: 'Partially impaired' },
      { value: '2', label: 'Severely impaired' }
    ]
  },
  {
    key: 'M1210_speechPatterns',
    label: 'Speech Patterns',
    type: 'select',
    required: true,
    category: OasisCategories.SENSORY_STATUS,
    oasisItem: 'M1210',
    options: [
      { value: '0', label: 'Clear speech' },
      { value: '1', label: 'Unclear speech' },
      { value: '2', label: 'Unable to speak' }
    ]
  },
  {
    key: 'M1220_hearingPatterns',
    label: 'Hearing',
    type: 'select',
    required: true,
    category: OasisCategories.SENSORY_STATUS,
    oasisItem: 'M1220',
    options: [
      { value: '0', label: 'Adequate' },
      { value: '1', label: 'Minimal difficulty' },
      { value: '2', label: 'Moderate difficulty' },
      { value: '3', label: 'Severe difficulty' }
    ]
  },

  // Pain Assessment - Critical for care planning
  {
    key: 'M1240_painAssessment',
    label: 'Pain Assessment',
    type: 'select',
    required: true,
    category: OasisCategories.SENSORY_STATUS,
    oasisItem: 'M1240',
    options: [
      { value: '0', label: 'No pain' },
      { value: '1', label: 'Pain that does not interfere with activity' },
      { value: '2', label: 'Pain that interferes with activity' },
      { value: '3', label: 'Pain that restricts activity' }
    ]
  },

  // Cognitive Function - Essential for care planning
  {
    key: 'M1700_cognitiveFunction',
    label: 'Cognitive Function',
    type: 'select',
    required: true,
    category: OasisCategories.NEURO_EMOTIONAL,
    oasisItem: 'M1700',
    options: [
      { value: '0', label: 'Alert/oriented, able to focus' },
      { value: '1', label: 'Requires prompting' },
      { value: '2', label: 'Requires assistance and some direction' },
      { value: '3', label: 'Requires considerable assistance' },
      { value: '4', label: 'Totally dependent' }
    ]
  },

  // ADL Items - Critical for HIPPS calculation
  {
    key: 'M1800_grooming',
    label: 'Grooming',
    type: 'select',
    required: true,
    category: OasisCategories.ADL_IADL,
    oasisItem: 'M1800',
    options: [
      { value: '0', label: 'Able to groom self' },
      { value: '1', label: 'Grooming utensils must be placed within reach' },
      { value: '2', label: 'Someone must assist' },
      { value: '3', label: 'Patient depends entirely upon someone else' }
    ]
  },
  {
    key: 'M1810_currentDressing',
    label: 'Current Ability to Dress Upper Body',
    type: 'select',
    required: true,
    category: OasisCategories.ADL_IADL,
    oasisItem: 'M1810',
    options: [
      { value: '0', label: 'Able to obtain, put on, and remove clothing' },
      { value: '1', label: 'Able to dress upper body without assistance' },
      { value: '2', label: 'Someone must help patient put on clothing' },
      { value: '3', label: 'Patient depends entirely upon another person' }
    ]
  },
  {
    key: 'M1820_currentBathing',
    label: 'Current Ability to Bathe',
    type: 'select',
    required: true,
    category: OasisCategories.ADL_IADL,
    oasisItem: 'M1820',
    options: [
      { value: '0', label: 'Able to bathe self in shower or tub' },
      { value: '1', label: 'With the use of devices' },
      { value: '2', label: 'Able to bathe in shower or tub with assistance' },
      { value: '3', label: 'Participates in bathing self in shower or tub' },
      { value: '4', label: 'Unable to use shower or tub' },
      { value: '5', label: 'Unable to bathe self' }
    ]
  },
  {
    key: 'M1830_currentToileting',
    label: 'Current Ability to Toilet',
    type: 'select',
    required: true,
    category: OasisCategories.ADL_IADL,
    oasisItem: 'M1830',
    options: [
      { value: '0', label: 'Able to get to and from toilet' },
      { value: '1', label: 'When reminded, assisted, or supervised' },
      { value: '2', label: 'Unable to get to and from toilet' },
      { value: '3', label: 'Unable to get to and from toilet or bedpan' }
    ]
  },
  {
    key: 'M1840_currentTransferring',
    label: 'Current Ability to Transfer',
    type: 'select',
    required: true,
    category: OasisCategories.ADL_IADL,
    oasisItem: 'M1840',
    options: [
      { value: '0', label: 'Able to independently transfer' },
      { value: '1', label: 'Transfers with minimal human assistance' },
      { value: '2', label: 'Unable to transfer self' },
      { value: '3', label: 'Unable to transfer self and is unable to bear weight' }
    ]
  },
  {
    key: 'M1845_currentAmbulation',
    label: 'Current Ability to Walk',
    type: 'select',
    required: true,
    category: OasisCategories.ADL_IADL,
    oasisItem: 'M1845',
    options: [
      { value: '0', label: 'Able to independently walk' },
      { value: '1', label: 'Requires use of device' },
      { value: '2', label: 'Able to walk only with supervision' },
      { value: '3', label: 'Chairfast, able to wheel self' },
      { value: '4', label: 'Chairfast, unable to wheel self' },
      { value: '5', label: 'Bedfast, unable to ambulate' }
    ]
  },

  // Medication Management - Important for care planning
  {
    key: 'M2020_managementOralMed',
    label: 'Management of Oral Medications',
    type: 'select',
    required: true,
    category: OasisCategories.MEDICATIONS,
    oasisItem: 'M2020',
    options: [
      { value: '0', label: 'Able to independently take correct oral medication' },
      { value: '1', label: 'Able to take medication if prepared' },
      { value: '2', label: 'Unable to take medication unless administered' }
    ]
  },

  // Care Management - Essential for episode planning
  {
    key: 'M2200_therapyNeedSoc',
    label: 'Therapy Need at Start of Care',
    type: 'select',
    required: true,
    category: OasisCategories.THERAPY_PLAN,
    oasisItem: 'M2200',
    options: [
      { value: '0', label: 'No therapy need' },
      { value: '1', label: 'Physical therapy' },
      { value: '2', label: 'Speech-language pathology' },
      { value: '3', label: 'Occupational therapy' },
      { value: '4', label: 'Physical and speech-language pathology' },
      { value: '5', label: 'Physical and occupational therapy' },
      { value: '6', label: 'Speech-language pathology and occupational therapy' },
      { value: '7', label: 'All three therapy services' }
    ]
  },

  // Discharge Items (for discharge assessments only)
  {
    key: 'M3000_dischargeTo',
    label: 'To Where Was Patient Discharged',
    type: 'select',
    required: false,
    category: OasisCategories.DISCHARGE,
    oasisItem: 'M3000',
    dependsOn: ['M0100_thisAssessmentCompleted'],
    options: [
      { value: '1', label: 'Patient remained in the community' },
      { value: '2', label: 'Patient transferred to a noninstitutional hospice' },
      { value: '3', label: 'Unknown because patient moved' },
      { value: '4', label: 'Patient transferred to an inpatient facility' },
      { value: '5', label: 'Patient died' }
    ]
  },
  {
    key: 'M3005_dischargeDate',
    label: 'Discharge Date',
    type: 'date',
    required: true,
    category: OasisCategories.DISCHARGE,
    oasisItem: 'M3005',
    dependsOn: ['M0100_thisAssessmentCompleted'],
    helpText: 'Date patient was discharged from home health services'
  },
  {
    key: 'M3010_dischargeReason',
    label: 'Reason for Discharge',
    type: 'select',
    required: true,
    category: OasisCategories.DISCHARGE,
    oasisItem: 'M3010',
    dependsOn: ['M0100_thisAssessmentCompleted'],
    options: [
      { value: '1', label: 'Goals achieved, no further need for services' },
      { value: '2', label: 'Patient refused services' },
      { value: '3', label: 'Patient moved out of service area' },
      { value: '4', label: 'Patient transferred to inpatient facility' },
      { value: '5', label: 'Physician orders discontinued' },
      { value: '6', label: 'Patient death' },
      { value: '7', label: 'Other reasons' }
    ]
  },
  {
    key: 'M3020_emergencyUse',
    label: 'Emergency Department Use',
    type: 'select',
    required: true,
    category: OasisCategories.DISCHARGE,
    oasisItem: 'M3020',
    dependsOn: ['M0100_thisAssessmentCompleted'],
    options: [
      { value: '0', label: 'No' },
      { value: '1', label: 'Yes, used emergency department without admission' },
      { value: '2', label: 'Yes, used emergency department with admission' },
      { value: '3', label: 'Yes, used emergency department, admission status unknown' },
      { value: '4', label: 'Unknown' }
    ],
    helpText: 'Did patient use emergency department during this episode?'
  },
  {
    key: 'M3025_emergencyUseAfter',
    label: 'Emergency Department Use After Discharge',
    type: 'select',
    required: false,
    category: OasisCategories.DISCHARGE,
    oasisItem: 'M3025',
    dependsOn: ['M0100_thisAssessmentCompleted'],
    options: [
      { value: '0', label: 'No' },
      { value: '1', label: 'Yes' },
      { value: '9', label: 'Not applicable' }
    ],
    helpText: 'Emergency department use in first 30 days after discharge'
  },
  {
    key: 'M3030_emergencyUseReasons',
    label: 'Reasons for Emergency Department Use',
    type: 'multiselect',
    required: false,
    category: OasisCategories.DISCHARGE,
    oasisItem: 'M3030',
    dependsOn: ['M3020_emergencyUse'],
    options: [
      { value: '1', label: 'Improper medication administration/side effects' },
      { value: '2', label: 'Injury caused by fall' },
      { value: '3', label: 'Respiratory issues' },
      { value: '4', label: 'Heart failure' },
      { value: '5', label: 'Other cardiovascular problems' },
      { value: '6', label: 'Wound infection or deterioration' },
      { value: '7', label: 'Hypo/hyperglycemia, diabetes complications' },
      { value: '8', label: 'GI bleeding, obstruction, constipation, impaction' },
      { value: '9', label: 'Dehydration, malnutrition' },
      { value: '10', label: 'Urinary tract infection' },
      { value: '11', label: 'Deep vein thrombosis, pulmonary embolism' },
      { value: '12', label: 'Shortness of breath' },
      { value: '13', label: 'Other reasons' }
    ]
  },
  // Discharge Functional Status (compare to admission)
  {
    key: 'M3100_dischargeGrooming',
    label: 'Grooming at Discharge',
    type: 'select',
    required: true,
    category: OasisCategories.DISCHARGE,
    oasisItem: 'M3100',
    dependsOn: ['M0100_thisAssessmentCompleted'],
    options: [
      { value: '0', label: 'Able to groom self' },
      { value: '1', label: 'Grooming utensils must be placed within reach' },
      { value: '2', label: 'Someone must assist' },
      { value: '3', label: 'Patient depends entirely upon someone else' }
    ]
  },
  {
    key: 'M3110_dischargeDressing',
    label: 'Dressing at Discharge',
    type: 'select',
    required: true,
    category: OasisCategories.DISCHARGE,
    oasisItem: 'M3110',
    dependsOn: ['M0100_thisAssessmentCompleted'],
    options: [
      { value: '0', label: 'Able to obtain, put on, and remove clothing' },
      { value: '1', label: 'Able to dress upper body without assistance' },
      { value: '2', label: 'Someone must help patient put on clothing' },
      { value: '3', label: 'Patient depends entirely upon another person' }
    ]
  },
  {
    key: 'M3120_dischargeBathing',
    label: 'Bathing at Discharge',
    type: 'select',
    required: true,
    category: OasisCategories.DISCHARGE,
    oasisItem: 'M3120',
    dependsOn: ['M0100_thisAssessmentCompleted'],
    options: [
      { value: '0', label: 'Able to bathe self in shower or tub' },
      { value: '1', label: 'With the use of devices' },
      { value: '2', label: 'Able to bathe in shower or tub with assistance' },
      { value: '3', label: 'Participates in bathing self in shower or tub' },
      { value: '4', label: 'Unable to use shower or tub' },
      { value: '5', label: 'Unable to bathe self' }
    ]
  },
  {
    key: 'M3130_dischargeTransferring',
    label: 'Transferring at Discharge',
    type: 'select',
    required: true,
    category: OasisCategories.DISCHARGE,
    oasisItem: 'M3130',
    dependsOn: ['M0100_thisAssessmentCompleted'],
    options: [
      { value: '0', label: 'Able to independently transfer' },
      { value: '1', label: 'Transfers with minimal human assistance' },
      { value: '2', label: 'Unable to transfer self' },
      { value: '3', label: 'Unable to transfer self and is unable to bear weight' }
    ]
  },
  {
    key: 'M3140_dischargeAmbulation',
    label: 'Ambulation at Discharge',
    type: 'select',
    required: true,
    category: OasisCategories.DISCHARGE,
    oasisItem: 'M3140',
    dependsOn: ['M0100_thisAssessmentCompleted'],
    options: [
      { value: '0', label: 'Able to independently walk' },
      { value: '1', label: 'Requires use of device' },
      { value: '2', label: 'Able to walk only with supervision' },
      { value: '3', label: 'Chairfast, able to wheel self' },
      { value: '4', label: 'Chairfast, unable to wheel self' },
      { value: '5', label: 'Bedfast, unable to ambulate' }
    ]
  }
];

// Helper function to get fields by category
export const getFieldsByCategory = (category: string): OasisFieldConfig[] => {
  return mvpOasisFields.filter(field => field.category === category);
};

// Helper function to get required fields
export const getRequiredFields = (): OasisFieldConfig[] => {
  return mvpOasisFields.filter(field => field.required);
};

// Helper function to get fields for specific assessment type
export const getFieldsForAssessmentType = (assessmentType: string): OasisFieldConfig[] => {
  // For discharge assessments, include discharge-specific fields
  if (assessmentType === 'discharge') {
    return mvpOasisFields;
  }
  
  // For SOC and ROC, exclude discharge-specific fields
  return mvpOasisFields.filter(field => field.category !== OasisCategories.DISCHARGE);
};

// Function to validate field dependencies
export const validateFieldDependencies = (responses: any, field: OasisFieldConfig): boolean => {
  if (!field.dependsOn || field.dependsOn.length === 0) {
    return true;
  }
  
  // Check if dependent fields have appropriate values
  return field.dependsOn.every(dependentField => {
    const value = responses[dependentField];
    
    // For discharge fields, check if assessment type indicates discharge/death/transfer
    if (field.category === OasisCategories.DISCHARGE) {
      if (dependentField === 'M0100_thisAssessmentCompleted') {
        return value === '7' || value === '8' || value === '9'; // Death, transfer, or discharge
      }
      
      // For emergency use reasons, check if emergency use was reported
      if (dependentField === 'M3020_emergencyUse') {
        return value === '1' || value === '2' || value === '3'; // Any emergency use
      }
    }
    
    return value !== undefined && value !== null && value !== '';
  });
};

// Helper function to determine if this is a discharge assessment based on M0100
export const isDischargeAssessment = (responses: any): boolean => {
  const assessmentType = responses?.['M0100_thisAssessmentCompleted'];
  return assessmentType === '7' || assessmentType === '8' || assessmentType === '9';
};
